import { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import {
  HomeRoute, PlansRoute, CompanyRoute, ContactRoute, TrustRoute, AiRoute,
  TradingRoute, PlatformRoute, WhiteLabelRoute, ResourcesRoute, GetStartedRoute,
  WlSignupSuccessRoute, PrivacyRoute, TermsRoute, NotFoundRoute,
} from "@/routes/marketingPages";

/* Route-change scroll manager: top on navigate, honour #hash anchors. */
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

/* One broken section must never blank the whole site. */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#050507] text-zinc-300 gap-3 p-8 text-center">
          <div className="text-brand-gold font-serif text-2xl">KALKI AI CLOUD</div>
          <p className="text-sm text-zinc-500">Something went wrong rendering this page.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white/5"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  /* Lenis momentum scrolling — one instance for the whole site. */
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/plans" element={<PlansRoute />} />
          <Route path="/pricing" element={<PlansRoute />} />
          <Route path="/company" element={<CompanyRoute />} />
          <Route path="/contact" element={<ContactRoute />} />
          <Route path="/trust" element={<TrustRoute />} />
          <Route path="/ai" element={<AiRoute />} />
          <Route path="/trading" element={<TradingRoute />} />
          <Route path="/platform" element={<PlatformRoute />} />
          <Route path="/white-label" element={<WhiteLabelRoute />} />
          <Route path="/resources" element={<ResourcesRoute />} />
          <Route path="/get-started" element={<GetStartedRoute />} />
          <Route path="/features" element={<Navigate to="/platform" replace />} />
          <Route path="/wl-signup/success" element={<WlSignupSuccessRoute />} />
          <Route path="/privacy" element={<PrivacyRoute />} />
          <Route path="/terms" element={<TermsRoute />} />
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
